import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserProfile, getUserMeasurements, saveUserMeasurements, getUserWorkoutPlans, saveUserWorkoutPlans, saveUserLastPlanUpdate, saveUserLastPlanSummary, saveUserWeeklyStrategy } from '../utils/userDataManager';
import { generateUserProgram } from '../utils/onboardingApi';
import { getNutritionEntries, calculateNutritionSummary } from '../utils/nutritionManager';
import './WeeklyReview.css';

function WeeklyReview() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [weekData, setWeekData] = useState(null);

  // Form state
  const [weight, setWeight] = useState('');
  const [bodyFat, setBodyFat] = useState('');
  const [pullups, setPullups] = useState('');
  const [pushups, setPushups] = useState('');

  // Body measurements
  const [biceps, setBiceps] = useState('');
  const [chest, setChest] = useState('');
  const [waist, setWaist] = useState('');
  const [hips, setHips] = useState('');
  const [thighs, setThighs] = useState('');
  const [calves, setCalves] = useState('');

  // Health metrics
  const [restingHeartRate, setRestingHeartRate] = useState('');
  const [bloodPressureSystolic, setBloodPressureSystolic] = useState('');
  const [bloodPressureDiastolic, setBloodPressureDiastolic] = useState('');

  // Recovery metrics
  const [avgSleepHours, setAvgSleepHours] = useState('');
  const [stressLevel, setStressLevel] = useState('');
  const [energyLevel, setEnergyLevel] = useState('');
  const [muscleSoreness, setMuscleSoreness] = useState(5);
  const [fatigueLevel, setFatigueLevel] = useState(5);

  useEffect(() => {
    loadWeekData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadWeekData = async () => {
    try {
      const userProfile = await getUserProfile();
      if (!userProfile) {
        navigate('/onboarding');
        return;
      }

      setProfile(userProfile);

      // Load current measurements as defaults
      setWeight(userProfile.weight || '');
      setBodyFat(userProfile.bodyFat || userProfile.body_fat || '');
      setPullups(userProfile.pullups || '');
      setPushups(userProfile.pushups || '');

      // Load body measurements (no need for dynamic import - already imported at top)
      const measurements = await getUserMeasurements();
      setBiceps(measurements.biceps || '');
      setChest(measurements.chest || '');
      setWaist(measurements.waist || '');
      setHips(measurements.hips || '');
      setThighs(measurements.thighs || '');
      setCalves(measurements.calves || '');
      setRestingHeartRate(measurements.restingHeartRate || '');
      setBloodPressureSystolic(measurements.bloodPressureSystolic || '');
      setBloodPressureDiastolic(measurements.bloodPressureDiastolic || '');
      setAvgSleepHours(measurements.avgSleepHours || '');
      setStressLevel(measurements.stressLevel || '');
      setEnergyLevel(measurements.energyLevel || '');
      setMuscleSoreness(measurements.muscleSoreness || 5);
      setFatigueLevel(measurements.fatigueLevel || 5);

      // Calculate week data
      const workoutPlans = await getUserWorkoutPlans();
      if (workoutPlans) {
        const weekInfo = await calculateWeekData(workoutPlans, measurements);
        setWeekData(weekInfo);
      }
    } catch (error) {
      console.error('Error loading week data:', error);
      setError('Failed to load data. Please try again.');
    }
  };

  const calculateWeekData = async (workoutPlans, previousMeasurements = {}) => {
    // Get workout history from localStorage
    const historyStr = localStorage.getItem('detailedWorkoutHistory');
    const history = historyStr ? JSON.parse(historyStr) : [];

    // Get last plan update date
    const lastUpdate = localStorage.getItem('lastPlanUpdate');
    const weekStartDate = lastUpdate ? new Date(lastUpdate) : new Date();

    // Filter workouts from this week
    const weekWorkouts = history.filter(w => {
      const workoutDate = new Date(w.date);
      return workoutDate >= weekStartDate;
    });

    // Count scheduled workouts (non-rest days)
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const scheduledCount = days.filter(day => {
      const workout = workoutPlans[day];
      return workout && workout.exercises && workout.exercises.length > 0;
    }).length;

    const completedCount = weekWorkouts.length;
    const totalExercises = weekWorkouts.reduce((sum, w) => sum + (w.exercises?.length || 0), 0);

    // Count skipped workouts
    const skippedCount = weekWorkouts.filter(w => w.skipped === true).length;

    // Generate performance summary
    const performanceSummary = generatePerformanceSummary(weekWorkouts);

    // Get nutrition targets from plan
    let totalCalories = 0;
    let totalProtein = 0;
    let daysWithNutrition = 0;

    days.forEach(day => {
      const workout = workoutPlans[day];
      if (workout?.calorieGoal) {
        totalCalories += workout.calorieGoal;
        daysWithNutrition++;
      }
      if (workout?.proteinGoal) {
        totalProtein += workout.proteinGoal;
      }
    });

    // Check if this is an assessment week
    const isAssessmentWeek = localStorage.getItem('isAssessmentWeek') === 'true';
    const assessmentWeekNumber = parseInt(localStorage.getItem('assessmentWeekNumber') || '0');

    // Extract detailed assessment data if this was Week 1
    let assessmentData = null;
    if (isAssessmentWeek && assessmentWeekNumber === 1) {
      assessmentData = extractAssessmentData(workoutPlans, weekWorkouts);
    }

    // Fetch nutrition data for the week
    const weekEndDate = new Date();
    const startDateStr = weekStartDate.toISOString().split('T')[0];
    const endDateStr = weekEndDate.toISOString().split('T')[0];

    const nutritionEntries = await getNutritionEntries(startDateStr, endDateStr);
    const avgCalorieGoal = daysWithNutrition > 0 ? Math.round(totalCalories / daysWithNutrition) : 0;
    const avgProteinGoal = daysWithNutrition > 0 ? Math.round(totalProtein / daysWithNutrition) : 0;

    const nutritionSummary = calculateNutritionSummary(nutritionEntries, {
      calorieGoal: avgCalorieGoal,
      proteinGoal: avgProteinGoal
    });

    // Create nutrition notes for AI
    let nutritionNotes = '';
    if (nutritionSummary.daysLogged > 0) {
      nutritionNotes = `User logged nutrition for ${nutritionSummary.daysLogged} of 7 days this week.\n`;
      nutritionNotes += `Average calorie intake: ${nutritionSummary.avgCalories} cal/day (target: ${avgCalorieGoal} cal/day, variance: ${nutritionSummary.calorieVariance > 0 ? '+' : ''}${nutritionSummary.calorieVariance} cal)\n`;
      nutritionNotes += `Average protein intake: ${nutritionSummary.avgProtein}g/day (target: ${avgProteinGoal}g/day, variance: ${nutritionSummary.proteinVariance > 0 ? '+' : ''}${nutritionSummary.proteinVariance}g)\n`;
      nutritionNotes += `Days on target (within ±10%): ${nutritionSummary.daysOnTarget} of ${nutritionSummary.daysLogged}\n`;

      if (nutritionSummary.calorieVariance > 100) {
        nutritionNotes += `⚠️ User is consistently OVER calorie target - may need to adjust goals or recommend smaller portions.\n`;
      } else if (nutritionSummary.calorieVariance < -100) {
        nutritionNotes += `⚠️ User is consistently UNDER calorie target - may be under-eating, risk of muscle loss and low energy.\n`;
      }

      if (nutritionSummary.proteinVariance < -20) {
        nutritionNotes += `⚠️ User is not meeting protein targets - recommend increasing protein intake for recovery and muscle building.\n`;
      }
    } else {
      nutritionNotes = 'User did not log nutrition this week. Encourage nutrition tracking for better results.';
    }

    return {
      weekNumber: assessmentWeekNumber || 1,
      startWeight: profile?.weight || weight,
      startBodyFat: profile?.bodyFat || bodyFat,
      startPullups: profile?.pullups || pullups,
      startPushups: profile?.pushups || pushups,
      startMeasurements: previousMeasurements,
      workoutsCompleted: completedCount,
      workoutsScheduled: scheduledCount,
      totalExercises: totalExercises,
      skippedWorkouts: skippedCount,
      performanceSummary: performanceSummary,
      avgCalorieTarget: avgCalorieGoal,
      avgProteinTarget: avgProteinGoal,
      nutritionSummary: nutritionSummary,
      nutritionNotes: nutritionNotes,
      isPostAssessmentWeek: isAssessmentWeek && assessmentWeekNumber === 1,
      assessmentData: assessmentData
    };
  };

  const extractAssessmentData = (workoutPlans, weekWorkouts) => {
    // Extract detailed assessment performance data
    const assessmentResults = {
      programName: workoutPlans.programName || 'Week 1 Assessment',
      assessmentFocus: workoutPlans.assessmentFocus || [],
      exercisePerformance: [],
      strengthBaselines: {},
      maxRepTests: {},
      conditioningTests: {},
      difficultyRatings: {},
      notes: []
    };

    // Aggregate all exercises from completed workouts
    const exerciseStats = {};

    weekWorkouts.forEach(workout => {
      const exercises = workout.exercises || [];
      exercises.forEach(exercise => {
        const name = exercise.name;
        if (!exerciseStats[name]) {
          exerciseStats[name] = {
            name: name,
            assessmentMetric: exercise.assessmentMetric || null,
            totalSets: 0,
            weights: [],
            reps: [],
            difficulties: [],
            times: [],
            distances: [],
            notes: []
          };
        }

        const stats = exerciseStats[name];

        if (exercise.sets && exercise.sets.length > 0) {
          stats.totalSets += exercise.sets.length;
          exercise.sets.forEach(set => {
            if (set.weight) stats.weights.push(parseFloat(set.weight));
            if (set.reps) stats.reps.push(parseInt(set.reps));
            if (set.time) stats.times.push(set.time);
            if (set.distance) stats.distances.push(set.distance);
          });
        }

        if (exercise.difficulty) {
          stats.difficulties.push(exercise.difficulty);
        }

        if (exercise.notes) {
          stats.notes.push(exercise.notes);
        }
      });
    });

    // Process each exercise and categorize by assessment metric
    Object.values(exerciseStats).forEach(stats => {
      const exerciseResult = {
        name: stats.name,
        assessmentMetric: stats.assessmentMetric,
        totalSets: stats.totalSets
      };

      // Calculate performance metrics
      if (stats.weights.length > 0) {
        exerciseResult.maxWeight = Math.max(...stats.weights);
        exerciseResult.avgWeight = (stats.weights.reduce((a, b) => a + b, 0) / stats.weights.length).toFixed(1);
      }

      if (stats.reps.length > 0) {
        exerciseResult.maxReps = Math.max(...stats.reps);
        exerciseResult.avgReps = Math.round(stats.reps.reduce((a, b) => a + b, 0) / stats.reps.length);
      }

      if (stats.difficulties.length > 0) {
        exerciseResult.avgDifficulty = (stats.difficulties.reduce((a, b) => a + b, 0) / stats.difficulties.length).toFixed(1);
      }

      if (stats.times.length > 0) {
        exerciseResult.bestTime = stats.times[0]; // Assuming best is first or should be min
        exerciseResult.avgTime = stats.times.join(', ');
      }

      if (stats.distances.length > 0) {
        exerciseResult.totalDistance = stats.distances.join(', ');
      }

      if (stats.notes.length > 0) {
        exerciseResult.notes = stats.notes.join('; ');
      }

      assessmentResults.exercisePerformance.push(exerciseResult);

      // Categorize by assessment metric type
      if (stats.assessmentMetric === 'max_reps') {
        assessmentResults.maxRepTests[stats.name] = {
          maxReps: exerciseResult.maxReps,
          difficulty: exerciseResult.avgDifficulty
        };
      } else if (stats.assessmentMetric === 'strength_baseline' || stats.assessmentMetric === '3rm_test') {
        assessmentResults.strengthBaselines[stats.name] = {
          maxWeight: exerciseResult.maxWeight,
          repsAtMax: exerciseResult.maxReps,
          avgDifficulty: exerciseResult.avgDifficulty
        };
      } else if (stats.assessmentMetric === 'cardio_baseline' || stats.assessmentMetric === 'time_trial') {
        assessmentResults.conditioningTests[stats.name] = {
          time: exerciseResult.bestTime || exerciseResult.avgTime,
          distance: exerciseResult.totalDistance,
          difficulty: exerciseResult.avgDifficulty
        };
      }

      // Track difficulty ratings for all exercises
      if (exerciseResult.avgDifficulty) {
        assessmentResults.difficultyRatings[stats.name] = parseFloat(exerciseResult.avgDifficulty);
      }
    });

    // Add summary notes
    const avgDifficulty = Object.values(assessmentResults.difficultyRatings).length > 0
      ? (Object.values(assessmentResults.difficultyRatings).reduce((a, b) => a + b, 0) / Object.values(assessmentResults.difficultyRatings).length).toFixed(1)
      : null;

    if (avgDifficulty) {
      assessmentResults.notes.push(`Average difficulty rating: ${avgDifficulty}/10`);
    }

    assessmentResults.notes.push(`Completed ${weekWorkouts.length} assessment workouts`);
    assessmentResults.notes.push(`Total exercises performed: ${Object.keys(exerciseStats).length}`);

    return assessmentResults;
  };

  const generatePerformanceSummary = (workouts) => {
    if (workouts.length === 0) return 'No workouts completed this week.';

    // Track exercise data by exercise name with workout-by-workout details
    const exerciseStats = {};

    workouts.forEach((workout, workoutIndex) => {
      const exercises = workout.exercises || [];
      exercises.forEach(exercise => {
        const name = exercise.name;
        if (!exerciseStats[name]) {
          exerciseStats[name] = {
            name: name,
            workoutSessions: [], // Track each workout session separately
            totalVolume: 0,
            allWeights: [],
            allReps: []
          };
        }

        const stats = exerciseStats[name];

        // Store this workout session's data
        const sessionData = {
          workoutNumber: workoutIndex + 1,
          date: workout.date,
          sets: [],
          difficulty: exercise.difficulty || null
        };

        if (exercise.sets && exercise.sets.length > 0) {
          exercise.sets.forEach(set => {
            const setData = {
              weight: set.weight ? parseFloat(set.weight) : 0,
              reps: set.reps ? parseInt(set.reps) : 0
            };
            sessionData.sets.push(setData);

            // Track overall stats
            stats.allWeights.push(setData.weight);
            stats.allReps.push(setData.reps);
            stats.totalVolume += setData.weight * setData.reps;
          });
        }

        stats.workoutSessions.push(sessionData);
      });
    });

    // Generate detailed summary with set-by-set progression
    let summary = `Completed ${workouts.length} workouts this week.\n\n`;
    summary += `DETAILED EXERCISE-BY-EXERCISE PERFORMANCE (SET-BY-SET DATA):\n\n`;

    Object.values(exerciseStats).forEach(stats => {
      const isBodyweight = stats.allWeights.every(w => w === 0);

      summary += `📊 ${stats.name}:\n`;
      summary += `   Performed ${stats.workoutSessions.length}x this week\n`;

      // Show each workout session
      stats.workoutSessions.forEach((session, idx) => {
        summary += `   Session ${session.workoutNumber}: `;

        if (session.sets.length > 0) {
          const setDetails = session.sets.map((set, setIdx) => {
            if (isBodyweight) {
              return `Set ${setIdx + 1}: ${set.reps} reps`;
            } else {
              return `Set ${setIdx + 1}: ${set.weight}lbs×${set.reps}`;
            }
          }).join(', ');

          summary += setDetails;

          if (session.difficulty) {
            summary += ` (difficulty: ${session.difficulty}/10)`;
          }
        } else {
          summary += 'No sets logged';
        }

        summary += '\n';
      });

      // Analyze progression patterns
      if (stats.workoutSessions.length >= 2) {
        const firstSession = stats.workoutSessions[0];
        const lastSession = stats.workoutSessions[stats.workoutSessions.length - 1];

        if (firstSession.sets.length > 0 && lastSession.sets.length > 0) {
          const firstMaxWeight = Math.max(...firstSession.sets.map(s => s.weight));
          const lastMaxWeight = Math.max(...lastSession.sets.map(s => s.weight));
          const firstMaxReps = Math.max(...firstSession.sets.map(s => s.reps));
          const lastMaxReps = Math.max(...lastSession.sets.map(s => s.reps));

          if (!isBodyweight && lastMaxWeight > firstMaxWeight) {
            summary += `   ✅ PROGRESSION: Increased max weight from ${firstMaxWeight}lbs to ${lastMaxWeight}lbs (+${lastMaxWeight - firstMaxWeight}lbs)\n`;
          } else if (lastMaxReps > firstMaxReps) {
            summary += `   ✅ PROGRESSION: Increased max reps from ${firstMaxReps} to ${lastMaxReps} (+${lastMaxReps - firstMaxReps} reps)\n`;
          } else if (!isBodyweight && lastMaxWeight === firstMaxWeight && lastMaxReps === firstMaxReps) {
            summary += `   ⚖️ MAINTAINED: Consistent performance across sessions\n`;
          }
        }
      }

      // Analyze fatigue patterns within sessions
      const sessionsWithMultipleSets = stats.workoutSessions.filter(s => s.sets.length >= 3);
      if (sessionsWithMultipleSets.length > 0) {
        const avgDropoffs = sessionsWithMultipleSets.map(session => {
          const firstSetReps = session.sets[0].reps;
          const lastSetReps = session.sets[session.sets.length - 1].reps;
          return ((firstSetReps - lastSetReps) / firstSetReps * 100);
        });

        const avgDropoff = avgDropoffs.reduce((a, b) => a + b, 0) / avgDropoffs.length;

        if (avgDropoff > 30) {
          summary += `   ⚠️ FATIGUE PATTERN: Rep performance drops ${avgDropoff.toFixed(0)}% from first to last set - may need more rest between sets or lighter weight\n`;
        } else if (avgDropoff < 10) {
          summary += `   💪 STRONG ENDURANCE: Minimal performance drop across sets - ready for progressive overload\n`;
        }
      }

      // Calculate total volume
      if (!isBodyweight && stats.totalVolume > 0) {
        summary += `   Total Volume: ${stats.totalVolume.toLocaleString()} lbs (weight × reps)\n`;
      }

      summary += '\n';
    });

    // Add weekly recommendations based on patterns
    summary += `\nWEEKLY PERFORMANCE INSIGHTS:\n`;
    const allDifficulties = [];
    Object.values(exerciseStats).forEach(stats => {
      stats.workoutSessions.forEach(session => {
        if (session.difficulty) allDifficulties.push(session.difficulty);
      });
    });

    if (allDifficulties.length > 0) {
      const avgDifficulty = allDifficulties.reduce((a, b) => a + b, 0) / allDifficulties.length;
      summary += `- Average difficulty rating: ${avgDifficulty.toFixed(1)}/10\n`;

      if (avgDifficulty >= 8.5) {
        summary += `- ⚠️ User is consistently rating exercises 8.5+/10 difficulty - consider reducing weight or volume to prevent overtraining\n`;
      } else if (avgDifficulty <= 5) {
        summary += `- 💪 User is rating exercises under 5/10 difficulty - ready for increased intensity (more weight/reps)\n`;
      } else if (avgDifficulty >= 6.5 && avgDifficulty <= 7.5) {
        summary += `- ✅ Optimal difficulty range (6.5-7.5/10) - maintain progressive overload strategy\n`;
      }
    }

    return summary;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Update profile with new measurements
      const updatedProfile = {
        ...profile,
        weight: parseFloat(weight),
        bodyFat: bodyFat ? parseFloat(bodyFat) : null,
        pullups: parseInt(pullups) || 0,
        pushups: parseInt(pushups) || 0
      };

      // Save measurements
      await saveUserMeasurements({
        weight: updatedProfile.weight,
        bodyFat: updatedProfile.bodyFat,
        pullups: updatedProfile.pullups,
        pushups: updatedProfile.pushups,
        biceps: biceps ? parseFloat(biceps) : null,
        chest: chest ? parseFloat(chest) : null,
        waist: waist ? parseFloat(waist) : null,
        hips: hips ? parseFloat(hips) : null,
        thighs: thighs ? parseFloat(thighs) : null,
        calves: calves ? parseFloat(calves) : null,
        restingHeartRate: restingHeartRate ? parseInt(restingHeartRate) : null,
        bloodPressureSystolic: bloodPressureSystolic ? parseInt(bloodPressureSystolic) : null,
        bloodPressureDiastolic: bloodPressureDiastolic ? parseInt(bloodPressureDiastolic) : null,
        avgSleepHours: avgSleepHours ? parseFloat(avgSleepHours) : null,
        stressLevel: stressLevel ? parseInt(stressLevel) : null,
        energyLevel: energyLevel ? parseInt(energyLevel) : null,
        muscleSoreness: muscleSoreness ? parseInt(muscleSoreness) : 5,
        fatigueLevel: fatigueLevel ? parseInt(fatigueLevel) : 5,
        date: new Date().toISOString()
      });

      // Prepare user data for AI
      // Parse JSON fields if they're strings (from Supabase)
      const parseJsonField = (field) => {
        if (!field) return [];
        if (Array.isArray(field)) return field;
        if (typeof field === 'string') {
          try {
            return JSON.parse(field);
          } catch {
            return [];
          }
        }
        return [];
      };

      const userData = {
        displayName: profile.displayName || profile.display_name,
        age: profile.age,
        gender: profile.gender,
        height: profile.height,
        weight: updatedProfile.weight,
        fitnessLevel: profile.fitnessLevel || profile.fitness_level,
        pullups: updatedProfile.pullups,
        pushups: updatedProfile.pushups,
        bodyFat: updatedProfile.bodyFat,
        primaryGoal: profile.primaryGoal || profile.primary_goal,
        targetAreas: parseJsonField(profile.targetAreas || profile.target_areas),
        timeframe: profile.timeframe || '12 weeks',
        daysPerWeek: profile.daysPerWeek || profile.days_per_week || 4,
        timePerSession: profile.timePerSession || profile.time_per_session || '45-60 min',
        homeWorkoutTime: profile.homeWorkoutTime || profile.home_workout_time || 'early morning',
        gymWorkoutTime: profile.gymWorkoutTime || profile.gym_workout_time || 'morning',
        gymDays: parseJsonField(profile.gymDays || profile.gym_days),
        restDays: parseJsonField(profile.restDays || profile.rest_days),
        gymAccess: parseJsonField(profile.gymAccess || profile.gym_access),
        equipment: parseJsonField(profile.equipment),
        // Current body measurements
        biceps: biceps ? parseFloat(biceps) : null,
        chest: chest ? parseFloat(chest) : null,
        waist: waist ? parseFloat(waist) : null,
        hips: hips ? parseFloat(hips) : null,
        thighs: thighs ? parseFloat(thighs) : null,
        calves: calves ? parseFloat(calves) : null,
        // Health metrics
        restingHeartRate: restingHeartRate ? parseInt(restingHeartRate) : null,
        bloodPressureSystolic: bloodPressureSystolic ? parseInt(bloodPressureSystolic) : null,
        bloodPressureDiastolic: bloodPressureDiastolic ? parseInt(bloodPressureDiastolic) : null,
        // Recovery metrics
        avgSleepHours: avgSleepHours ? parseFloat(avgSleepHours) : null,
        stressLevel: stressLevel ? parseInt(stressLevel) : null,
        energyLevel: energyLevel ? parseInt(energyLevel) : null,
        muscleSoreness: muscleSoreness ? parseInt(muscleSoreness) : 5,
        fatigueLevel: fatigueLevel ? parseInt(fatigueLevel) : 5
      };

      // Generate new program with previous week's data
      const newProgram = await generateUserProgram(userData, weekData);

      // Save the new program
      await saveUserWorkoutPlans(newProgram.weeklyPlan);
      await saveUserLastPlanUpdate(new Date().toISOString());
      await saveUserLastPlanSummary(newProgram.programSummary);
      if (newProgram.weeklyStrategyOverview) {
        await saveUserWeeklyStrategy(newProgram.weeklyStrategyOverview);
      }

      // Clear assessment week flags if we just completed Week 1
      if (weekData.isPostAssessmentWeek) {
        localStorage.setItem('isAssessmentWeek', 'false');
        localStorage.setItem('assessmentWeekNumber', '2');
      }

      // Navigate to feedback page if available
      if (newProgram.weeklyFeedback) {
        navigate('/weekly-feedback', {
          state: {
            feedback: newProgram.weeklyFeedback,
            programSummary: newProgram.programSummary
          }
        });
      } else {
        alert('Week ' + (weekData.weekNumber + 1) + ' program generated! Check your schedule.');
        navigate('/');
      }
    } catch (err) {
      console.error('Error generating new program:', err);
      setError(err.message || 'Failed to generate new program');
    } finally {
      setLoading(false);
    }
  };

  if (!profile) {
    return <div className="container">Loading...</div>;
  }

  return (
    <div className="container">
      <div className="weekly-review-screen">
        <div className="review-header">
          <button className="back-button" onClick={() => navigate('/')}>← Back</button>
          <h2>📊 Weekly Review</h2>
          <p className="review-subtitle">Time to update your measurements and plan Week {weekData ? weekData.weekNumber + 1 : 2}!</p>
        </div>

        {weekData && (
          <div className="week-summary">
            <h3>Week {weekData.weekNumber} Summary</h3>
            <div className="summary-stats">
              <div className="summary-stat">
                <span className="stat-icon">✅</span>
                <div>
                  <p className="stat-value">{weekData.workoutsCompleted}/{weekData.workoutsScheduled}</p>
                  <p className="stat-label">Workouts Completed</p>
                </div>
              </div>
              <div className="summary-stat">
                <span className="stat-icon">💪</span>
                <div>
                  <p className="stat-value">{weekData.totalExercises}</p>
                  <p className="stat-label">Total Exercises</p>
                </div>
              </div>
            </div>
            {weekData.performanceSummary && (
              <p className="performance-note">{weekData.performanceSummary}</p>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="review-form">
          <h3>Update Your Measurements</h3>
          <p className="form-note">Enter your current measurements. The AI will analyze your progress and adjust your program.</p>

          <div className="form-group">
            <label>Weight (lbs) *</label>
            <input
              type="number"
              step="0.1"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              required
              placeholder="e.g., 175.5"
            />
            {profile.weight && (
              <span className="previous-value">Previous: {profile.weight} lbs</span>
            )}
          </div>

          <div className="form-group">
            <label>Body Fat % (optional)</label>
            <input
              type="number"
              step="0.1"
              value={bodyFat}
              onChange={(e) => setBodyFat(e.target.value)}
              placeholder="e.g., 20.5"
            />
            {profile.bodyFat && (
              <span className="previous-value">Previous: {profile.bodyFat}%</span>
            )}
          </div>

          <div className="form-group">
            <label>Max Pull-ups *</label>
            <input
              type="number"
              value={pullups}
              onChange={(e) => setPullups(e.target.value)}
              required
              placeholder="e.g., 5"
            />
            {profile.pullups && (
              <span className="previous-value">Previous: {profile.pullups}</span>
            )}
          </div>

          <div className="form-group">
            <label>Max Push-ups *</label>
            <input
              type="number"
              value={pushups}
              onChange={(e) => setPushups(e.target.value)}
              required
              placeholder="e.g., 30"
            />
            {profile.pushups && (
              <span className="previous-value">Previous: {profile.pushups}</span>
            )}
          </div>

          <h3 style={{ marginTop: '30px' }}>Body Measurements (optional)</h3>
          <p className="form-note">Track changes in muscle size and body composition. All measurements in inches.</p>

          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div className="form-group">
              <label>Biceps (inches)</label>
              <input
                type="number"
                step="0.1"
                value={biceps}
                onChange={(e) => setBiceps(e.target.value)}
                placeholder="e.g., 14.5"
              />
              {weekData?.startMeasurements?.biceps && (
                <span className="previous-value">Previous: {weekData.startMeasurements.biceps}"</span>
              )}
            </div>

            <div className="form-group">
              <label>Chest (inches)</label>
              <input
                type="number"
                step="0.1"
                value={chest}
                onChange={(e) => setChest(e.target.value)}
                placeholder="e.g., 40.0"
              />
              {weekData?.startMeasurements?.chest && (
                <span className="previous-value">Previous: {weekData.startMeasurements.chest}"</span>
              )}
            </div>

            <div className="form-group">
              <label>Waist (inches)</label>
              <input
                type="number"
                step="0.1"
                value={waist}
                onChange={(e) => setWaist(e.target.value)}
                placeholder="e.g., 32.0"
              />
              {weekData?.startMeasurements?.waist && (
                <span className="previous-value">Previous: {weekData.startMeasurements.waist}"</span>
              )}
            </div>

            <div className="form-group">
              <label>Hips (inches)</label>
              <input
                type="number"
                step="0.1"
                value={hips}
                onChange={(e) => setHips(e.target.value)}
                placeholder="e.g., 38.0"
              />
              {weekData?.startMeasurements?.hips && (
                <span className="previous-value">Previous: {weekData.startMeasurements.hips}"</span>
              )}
            </div>

            <div className="form-group">
              <label>Thighs (inches)</label>
              <input
                type="number"
                step="0.1"
                value={thighs}
                onChange={(e) => setThighs(e.target.value)}
                placeholder="e.g., 23.0"
              />
              {weekData?.startMeasurements?.thighs && (
                <span className="previous-value">Previous: {weekData.startMeasurements.thighs}"</span>
              )}
            </div>

            <div className="form-group">
              <label>Calves (inches)</label>
              <input
                type="number"
                step="0.1"
                value={calves}
                onChange={(e) => setCalves(e.target.value)}
                placeholder="e.g., 15.0"
              />
              {weekData?.startMeasurements?.calves && (
                <span className="previous-value">Previous: {weekData.startMeasurements.calves}"</span>
              )}
            </div>
          </div>

          <h3 style={{ marginTop: '30px' }}>Recovery & Health Metrics</h3>
          <p className="form-note">Track sleep, stress, and cardiovascular health - critical for progress!</p>

          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div className="form-group">
              <label>Average Sleep (hours/night)</label>
              <input
                type="number"
                step="0.5"
                value={avgSleepHours}
                onChange={(e) => setAvgSleepHours(e.target.value)}
                placeholder="e.g., 7.5"
              />
              {weekData?.startMeasurements?.avgSleepHours && (
                <span className="previous-value">Previous: {weekData.startMeasurements.avgSleepHours} hrs</span>
              )}
              <span className="field-hint" style={{ fontSize: '12px', color: '#888', display: 'block', marginTop: '4px' }}>
                Target: 7-9 hours for optimal recovery
              </span>
            </div>

            <div className="form-group">
              <label>Stress Level (1-10)</label>
              <input
                type="number"
                min="1"
                max="10"
                value={stressLevel}
                onChange={(e) => setStressLevel(e.target.value)}
                placeholder="1 = very calm, 10 = very stressed"
              />
              {weekData?.startMeasurements?.stressLevel && (
                <span className="previous-value">Previous: {weekData.startMeasurements.stressLevel}/10</span>
              )}
            </div>

            <div className="form-group">
              <label>Average Energy Level (1-10)</label>
              <input
                type="number"
                min="1"
                max="10"
                value={energyLevel}
                onChange={(e) => setEnergyLevel(e.target.value)}
                placeholder="1 = exhausted, 10 = very energetic"
              />
              {weekData?.startMeasurements?.energyLevel && (
                <span className="previous-value">Previous: {weekData.startMeasurements.energyLevel}/10</span>
              )}
            </div>

            <div className="form-group">
              <label>Resting Heart Rate (bpm)</label>
              <input
                type="number"
                value={restingHeartRate}
                onChange={(e) => setRestingHeartRate(e.target.value)}
                placeholder="e.g., 65"
              />
              {weekData?.startMeasurements?.restingHeartRate && (
                <span className="previous-value">Previous: {weekData.startMeasurements.restingHeartRate} bpm</span>
              )}
            </div>

            <div className="form-group">
              <label>Blood Pressure (systolic/diastolic)</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="number"
                  value={bloodPressureSystolic}
                  onChange={(e) => setBloodPressureSystolic(e.target.value)}
                  placeholder="120"
                  style={{ flex: 1 }}
                />
                <span style={{ color: '#888' }}>/</span>
                <input
                  type="number"
                  value={bloodPressureDiastolic}
                  onChange={(e) => setBloodPressureDiastolic(e.target.value)}
                  placeholder="80"
                  style={{ flex: 1 }}
                />
              </div>
              {weekData?.startMeasurements?.bloodPressureSystolic && weekData?.startMeasurements?.bloodPressureDiastolic && (
                <span className="previous-value">
                  Previous: {weekData.startMeasurements.bloodPressureSystolic}/{weekData.startMeasurements.bloodPressureDiastolic}
                </span>
              )}
            </div>
          </div>

          <h3 style={{ marginTop: '30px' }}>💪 Muscle Soreness & Fatigue</h3>
          <p className="form-note">How is your body feeling this week? This helps AI adjust intensity for next week.</p>

          <div className="slider-group">
            <label className="slider-label">
              Muscle Soreness: {muscleSoreness}/10
              <span className="slider-description">
                {muscleSoreness <= 3 && ' - Feeling great, minimal soreness'}
                {muscleSoreness >= 4 && muscleSoreness <= 6 && ' - Normal soreness, recovering well'}
                {muscleSoreness >= 7 && muscleSoreness <= 8 && ' - Quite sore, need recovery'}
                {muscleSoreness >= 9 && ' - Very sore, overtraining risk'}
              </span>
            </label>
            <div className="slider-container">
              <span className="slider-min">1 - No soreness</span>
              <input
                type="range"
                min="1"
                max="10"
                value={muscleSoreness}
                onChange={(e) => setMuscleSoreness(parseInt(e.target.value))}
                className="recovery-slider"
                style={{
                  background: `linear-gradient(to right, #4ECDC4 0%, #4ECDC4 ${((muscleSoreness - 1) / 9) * 100}%, #3a3a3a ${((muscleSoreness - 1) / 9) * 100}%, #3a3a3a 100%)`
                }}
              />
              <span className="slider-max">10 - Extremely sore</span>
            </div>
            {weekData?.startMeasurements?.muscleSoreness && (
              <span className="previous-value">Previous: {weekData.startMeasurements.muscleSoreness}/10</span>
            )}
          </div>

          <div className="slider-group">
            <label className="slider-label">
              Overall Fatigue: {fatigueLevel}/10
              <span className="slider-description">
                {fatigueLevel <= 3 && ' - Full of energy'}
                {fatigueLevel >= 4 && fatigueLevel <= 6 && ' - Normal fatigue, manageable'}
                {fatigueLevel >= 7 && fatigueLevel <= 8 && ' - Very tired, need more rest'}
                {fatigueLevel >= 9 && ' - Exhausted, risk of burnout'}
              </span>
            </label>
            <div className="slider-container">
              <span className="slider-min">1 - Fully energized</span>
              <input
                type="range"
                min="1"
                max="10"
                value={fatigueLevel}
                onChange={(e) => setFatigueLevel(parseInt(e.target.value))}
                className="recovery-slider"
                style={{
                  background: `linear-gradient(to right, #4ECDC4 0%, #4ECDC4 ${((fatigueLevel - 1) / 9) * 100}%, #3a3a3a ${((fatigueLevel - 1) / 9) * 100}%, #3a3a3a 100%)`
                }}
              />
              <span className="slider-max">10 - Completely exhausted</span>
            </div>
            {weekData?.startMeasurements?.fatigueLevel && (
              <span className="previous-value">Previous: {weekData.startMeasurements.fatigueLevel}/10</span>
            )}
          </div>

          {error && (
            <div className="error-message">
              <p>⚠️ {error}</p>
            </div>
          )}

          <button type="submit" className="generate-button" disabled={loading}>
            {loading ? '🤖 Analyzing Progress & Generating Next Week...' : '🚀 Generate Week ' + (weekData ? weekData.weekNumber + 1 : 2) + ' Program'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default WeeklyReview;
